import React from 'react'
import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators'

import { getRangeFromDateFilter } from 'utils/dateUtils'
import { beautifyObjectKeys } from 'utils/helpers'
import { navigateTo } from 'utils/navigation'
import { URL_CONST } from 'utils/urlConsts'
import { matchURLStartsWith } from 'utils/urls'

import { GET_BILLING_STATUS_ASYNC } from 'containers/Agents/constants'
import { selectActualTimezone } from 'containers/App/selectors'
import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'
import { addToast } from 'containers/Toasts/actions'

import UnsubscribeCompleteToast from 'components/pages/settings/creditManagement/Billing/UnsubscribeCompleteToast'

import {
  CANCEL_SUBSCRIPTION_ASYNC,
  CREATE_PORTAL_SESSION_ASYNC,
  GET_CHECKOUT_SESSION_ASYNC,
  GET_CREDITS_OVERVIEW_ASYNC,
  GET_HISTORY_LOG_ASYNC,
  GET_INVOICES_ASYNC,
  GET_PRICING_ASYNC,
  GET_SETUP_INTENT_ASYNC,
  SET_HISTORICAL_USAGE_FILTER,
  SET_HISTORY_FILTER,
  SET_INVOICES_SORT,
  UNSUBSCRIBE_COMPLETE_TOAST_ID,
  UNSUBSCRIBE_COMPLETE_TOAST_MS,
  UPDATE_MONTHLY_LIMIT_ASYNC,
} from './constants'

const getDateRange = (state$) => {
  const { dateFilter, customStartDate, customEndDate } =
    state$.value.settings.creditsManagement.self.historical.filters
  if (Array.isArray(dateFilter) && dateFilter.length === 2) return dateFilter
  if (customStartDate && customEndDate) return [customStartDate, customEndDate]
  return getRangeFromDateFilter(dateFilter) ?? []
}

const getCreditsOverviewEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      GET_CREDITS_OVERVIEW_ASYNC.INITIATE,
      SET_HISTORICAL_USAGE_FILTER,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS) {
        return matchURLStartsWith(URL_CONST.settings.creditsSettings)
      }
      return true
    }),
    switchMap(() => {
      const companyFilter = selectCompanyFilter(state$.value)?.value
      const timezone = selectActualTimezone(state$.value)
      return api.creditManagement.getOverview(companyFilter, getDateRange(state$), timezone).pipe(
        map(({ response }) => GET_CREDITS_OVERVIEW_ASYNC.onSuccess(response)),
        catchError((error) => of(epicsErrorHandler(GET_CREDITS_OVERVIEW_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(GET_CREDITS_OVERVIEW_ASYNC.CANCEL)))
      )
    })
  )

const buildHistoryLogPayload = (state$) => {
  const { dateFilter, search, agents, page, sortBy, sortDir } =
    state$.value.settings.creditsManagement.self.history.filters

  let dateRange = []
  if (Array.isArray(dateFilter) && dateFilter.length === 2) {
    dateRange = dateFilter
  } else {
    dateRange = getRangeFromDateFilter(dateFilter) ?? []
  }

  return {
    date_range: dateRange,
    timezone: selectActualTimezone(state$.value),
    search: search || '',
    agents: agents.includes('all') ? [] : agents,
    sort_by: sortBy || 'datetime',
    sort_dir: sortDir || 'desc',
    page: page || 1,
  }
}

const getHistoryLogEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_HISTORY_LOG_ASYNC.INITIATE, SET_HISTORY_FILTER),
    switchMap(() => {
      const companyFilter = selectCompanyFilter(state$.value)?.value
      const payload = buildHistoryLogPayload(state$)
      return api.creditManagement.getHistoryLog(companyFilter, payload).pipe(
        map(({ response }) => GET_HISTORY_LOG_ASYNC.onSuccess(response)),
        catchError((error) => of(epicsErrorHandler(GET_HISTORY_LOG_ASYNC.onError, error, []))),
        takeUntil(action$.pipe(ofType(GET_HISTORY_LOG_ASYNC.CANCEL)))
      )
    })
  )

const getInvoicesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_INVOICES_ASYNC.INITIATE, SET_INVOICES_SORT),
    switchMap(() => {
      const companyFilter = selectCompanyFilter(state$.value)?.value
      const { sortBy, sortDir } = state$.value.settings.creditsManagement.self.billing.invoiceSort
      return api.creditManagement.getInvoices(companyFilter, { sortBy, sortDir }).pipe(
        map((response) => GET_INVOICES_ASYNC.onSuccess(beautifyObjectKeys(response))),
        catchError((error) => of(epicsErrorHandler(GET_INVOICES_ASYNC.onError, error, [404]))),
        takeUntil(action$.pipe(ofType(GET_INVOICES_ASYNC.CANCEL)))
      )
    })
  )

const getPricingEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_PRICING_ASYNC.INITIATE),
    switchMap(() => {
      const companyFilter = selectCompanyFilter(state$.value)?.value
      return api.creditManagement.getPricing(companyFilter).pipe(
        map(({ response }) => GET_PRICING_ASYNC.onSuccess(response)),
        catchError((error) => of(epicsErrorHandler(GET_PRICING_ASYNC.onError, error, [503]))),
        takeUntil(action$.pipe(ofType(GET_PRICING_ASYNC.CANCEL)))
      )
    })
  )

const getSetupIntentEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_SETUP_INTENT_ASYNC.INITIATE),
    switchMap(({ payload }) => {
      const companyFilter = selectCompanyFilter(state$.value)?.value
      return api.creditManagement.getSetupIntent(companyFilter).pipe(
        map(({ response }) => {
          // The company filter can change while this request is in flight; redirecting to a
          // setup-intent URL scoped to the company active when the request STARTED, once a
          // different company is active, would send the user to the wrong company's payment page.
          const stillSameCompany = selectCompanyFilter(state$.value)?.value === companyFilter
          if (payload?.redirect && response?.url && stillSameCompany) {
            navigateTo(response.url)
          }
          return GET_SETUP_INTENT_ASYNC.onSuccess(response)
        }),
        catchError((error) => of(epicsErrorHandler(GET_SETUP_INTENT_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(GET_SETUP_INTENT_ASYNC.CANCEL)))
      )
    })
  )

const getCheckoutSessionEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_CHECKOUT_SESSION_ASYNC.INITIATE),
    switchMap(({ payload: { sessionId } }) => {
      const companyFilter = selectCompanyFilter(state$.value)?.value
      return api.creditManagement.getCheckoutSession(sessionId, companyFilter).pipe(
        mergeMap(({ response }) =>
          of(GET_CHECKOUT_SESSION_ASYNC.onSuccess(response), GET_BILLING_STATUS_ASYNC.initiate())
        ),
        catchError((error) => of(epicsErrorHandler(GET_CHECKOUT_SESSION_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(GET_CHECKOUT_SESSION_ASYNC.CANCEL)))
      )
    })
  )

const updateMonthlyLimitEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_MONTHLY_LIMIT_ASYNC.INITIATE),
    switchMap(({ payload: { monthlyLimit } }) => {
      const companyFilter = selectCompanyFilter(state$.value)?.value
      return api.creditManagement.updateMonthlyLimit(monthlyLimit, companyFilter).pipe(
        // PUT /billing/limit echoes only { monthlyLimit }; refetch so
        // limitUsage (and the 80% alert) is not left at the pre-raise %.
        mergeMap(({ response }) =>
          of(
            UPDATE_MONTHLY_LIMIT_ASYNC.onSuccess(beautifyObjectKeys(response)),
            GET_BILLING_STATUS_ASYNC.initiate()
          )
        ),
        catchError((error) => of(epicsErrorHandler(UPDATE_MONTHLY_LIMIT_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(UPDATE_MONTHLY_LIMIT_ASYNC.CANCEL)))
      )
    })
  )

const PORTAL_SESSION_ERROR_MESSAGE = 'Unable to open the payment portal. Please try again.'

const createPortalSessionEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_PORTAL_SESSION_ASYNC.INITIATE),
    switchMap(() => {
      const companyFilter = selectCompanyFilter(state$.value)?.value
      return api.creditManagement.createPortalSession(companyFilter).pipe(
        map(({ response }) => CREATE_PORTAL_SESSION_ASYNC.onSuccess(response)),
        catchError((error) =>
          of(
            epicsErrorHandler(CREATE_PORTAL_SESSION_ASYNC.onError, error),
            addToast({ message: PORTAL_SESSION_ERROR_MESSAGE, type: 'error' })
          )
        ),
        takeUntil(action$.pipe(ofType(CREATE_PORTAL_SESSION_ASYNC.CANCEL)))
      )
    })
  )

const cancelSubscriptionEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CANCEL_SUBSCRIPTION_ASYNC.INITIATE),
    switchMap(() => {
      const companyFilter = selectCompanyFilter(state$.value)?.value
      return api.creditManagement.cancelSubscription(companyFilter).pipe(
        mergeMap(({ response }) =>
          of(
            CANCEL_SUBSCRIPTION_ASYNC.onSuccess(response),
            addToast({
              id: UNSUBSCRIBE_COMPLETE_TOAST_ID,
              timeToDisappear: UNSUBSCRIBE_COMPLETE_TOAST_MS,
              component: React.createElement(UnsubscribeCompleteToast, {
                id: UNSUBSCRIBE_COMPLETE_TOAST_ID,
              }),
            }),
            GET_BILLING_STATUS_ASYNC.initiate()
          )
        ),
        catchError((error) => of(epicsErrorHandler(CANCEL_SUBSCRIPTION_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(CANCEL_SUBSCRIPTION_ASYNC.CANCEL)))
      )
    })
  )

export default combineEpics(
  getCreditsOverviewEpic,
  getHistoryLogEpic,
  getInvoicesEpic,
  getPricingEpic,
  getSetupIntentEpic,
  getCheckoutSessionEpic,
  updateMonthlyLimitEpic,
  createPortalSessionEpic,
  cancelSubscriptionEpic
)
